import { type FC } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Image } from '@chakra-ui/react';
import { type ChallengeRequestScreenState } from '@shinetools/sca-operations';
import {
  Button,
  Typography,
  View,
  YStack,
} from '@shinetools/sunshine-universal';

import { ChallengeStateName } from '__generated__/GQL';
import fadedFlower from 'assets/brand/faded-flower.png';
import SunshineModal from 'components/_core/SunshineModal';
import { useVerifyChallenge } from 'components/StrongAuthenticationChallengeModal/hooks/useVerifyChallenge';

import { modalMinHeight } from '../utils/constant';

export interface ChallengeRequestDeclinedProps {
  challengeId: string;
  operation: string;
  userId: string;
  moveState: (state: ChallengeRequestScreenState) => void;
  onClose: () => void;
}

const ChallengeRequestDeclined: FC<ChallengeRequestDeclinedProps> = ({
  challengeId,
  onClose,
  operation,
  userId,
}) => {
  const { push } = useHistory();

  if (!challengeId || !operation || !userId) {
    throw new Error('Missing challengeId, name, operation or userId');
  }

  const { decline } = useVerifyChallenge();

  const handleUserDecline = useCallback(async () => {
    try {
      await decline({
        challengeId,
        name: ChallengeStateName.ChallengeDeclined,
      });
      onClose();
    } catch (err) {
      onClose();
    }
  }, [challengeId, decline, onClose]);

  const handleUserDeclineNotInitiator = useCallback(async () => {
    try {
      await decline({
        challengeId,
        name: ChallengeStateName.ChallengeDeclinedNotInitiator,
      });
      push(`/help/urgent`);
    } catch (err) {
      push(`/help/urgent`);
    }
  }, [push, challengeId, decline]);

  return (
    <>
      <SunshineModal.Body minHeight={modalMinHeight}>
        <YStack gap={24} paddingBottom="$space.24">
          <YStack alignItems="center">
            <Image
              src={fadedFlower}
              style={{
                marginBottom: 24,
                opacity: 1,
                width: '30%',
              }}
            />
          </YStack>

          <Typography.Header>
            <FormattedMessage id="authentication.challenge.declined.title" />
          </Typography.Header>
          <Typography.Text>
            <FormattedMessage id="authentication.challenge.declined.description" />
          </Typography.Text>
        </YStack>
      </SunshineModal.Body>

      <SunshineModal.Footer flexDirection="row" gap={16}>
        <View>
          <Button onPress={handleUserDecline} variant="tertiary">
            <FormattedMessage id="authentication.close" />
          </Button>
        </View>

        <View marginLeft="40%">
          <Button onPress={handleUserDeclineNotInitiator}>
            <FormattedMessage id="authentication.challenge.declined.contactSupport" />
          </Button>
        </View>
      </SunshineModal.Footer>
    </>
  );
};

export default ChallengeRequestDeclined;
